import { flatten } from "lodash";

import { rulesEngineSelectors } from "@dndbeyond/character-rules-engine";

import config from "~/config";
import { NavigationType, RouteKey } from "~/subApps/builder/constants";

import * as navConfig from "../config/navigation";
import { builderSelectors } from "../selectors";

const BASE_PATHNAME = config.basePathname;

function checkIsRouteInNavConfig(targetRouteDef, navConfig) {
  const routeDefs: Array<any> = flatten(
    navConfig.map((section) => section.routes)
  );
  return routeDefs.filter((routeDef) => routeDef.key === targetRouteDef.key)
    .length;
}

export function checkStdBuilderPageRequirements(routeDef, state) {
  return (
    builderSelectors.getBuilderMethod(state) !== null &&
    checkIsRouteInNavConfig(
      routeDef,
      navConfig.getNavBuilderConfig(
        builderSelectors.getBuilderMethod(state),
        builderSelectors.getIsBuilderFlowExperimentEnabled(state)
      )
    )
  );
}

export function checkIsCurrentRouteInRoutePathing(state) {
  const currentPath = state.routeControls.currentPath;
  const characterId = rulesEngineSelectors.getId(state);

  // The settings page lives outside the route pathing, but landing on it shouldn't redirect away
  if (builderSelectors.getIsBuilderFlowExperimentEnabled(state)) {
    const settingsPath = navConfig
      .getRouteDef(RouteKey.HOME_BASIC_INFO)
      .path.replace(":characterId", characterId);
    if (currentPath === settingsPath) {
      return true;
    }
  }

  const routePathing = builderSelectors.getRoutePathing(state);
  const currentRoutePathIdx = getRoutePathIdxByPath(
    currentPath,
    routePathing,
    characterId
  );
  return currentRoutePathIdx > 0;
}

// TODO: This function seems to jsut return '/' so investigate it and remove it if it's not needed
export const getAvailableRouteRedirect = (routeKey, state) => {
  const routePath = navConfig.getRouteDefPath(routeKey);
  const routePathing = builderSelectors.getRoutePathing(state);
  const currentRoutePathIdx = getRoutePathIdxByPath(routePath, routePathing);

  const currentRoutePathingNode = routePathing[currentRoutePathIdx];

  if (currentRoutePathIdx === -1 || !currentRoutePathingNode.available) {
    return "/";
  }

  return "/";
};

export const getRoutePathIdx = (routeKey, routePathDef) => {
  const path = navConfig.getRouteDefPath(routeKey);
  return routePathDef.findIndex((routeDef) => path === routeDef.path);
};

export function getRoutePathIdxByPath(path, routePathDef, characterId = 0) {
  return routePathDef.findIndex(
    (routeDef) => path === routeDef.path.replace(":characterId", characterId)
  );
}

export function getCurrentSectionHelpRoutePath(state) {
  const currentPath = builderSelectors.getCurrentPath();
  const sections = navConfig.getNavigationSections(
    builderSelectors.getBuilderMethod(state),
    navConfig.getCurrentRouteDef(currentPath),
    builderSelectors.getIsBuilderFlowExperimentEnabled(state)
  );
  const currentSection = sections.find((section) => section.active);
  // No section is active on the settings page, so there's no help page to go to
  if (!currentSection) {
    return null;
  }
  const currentSectionHelpRoute = currentSection.routes.find(
    (route) => route.type === NavigationType.HelpPage
  );
  //TODO change this to getRouteDefPath once it takes a routeDef instead of routeKey
  if (currentSectionHelpRoute) {
    return currentSectionHelpRoute.path;
  } else {
    return null;
  }
}

export function getAvailablePathRedirect(state) {
  const currentPath = builderSelectors.getCurrentPath();
  const currentSearch = builderSelectors.getCurrentSearchParams();
  const routePathing = builderSelectors.getRoutePathing(state);
  const characterId = rulesEngineSelectors.getId(state);
  const currentRoutePathIdx = getRoutePathIdxByPath(
    `${currentPath}${currentSearch}`,
    routePathing,
    characterId
  );

  // The current page isn't in the route pathing (e.g. the settings page), so stay put
  if (currentRoutePathIdx === -1) {
    return null;
  }

  const currentRoutePathingNode = routePathing[currentRoutePathIdx];

  if (!currentRoutePathingNode.available) {
    for (let i = currentRoutePathIdx; i < routePathing.length; i++) {
      if (routePathing[i].available) {
        return routePathing[i].path;
      }
    }
  }

  return null;
}

export function getCharacterBuilderUrl(characterId): string {
  return getUrlWithParams(`${BASE_PATHNAME}/${characterId}/builder`);
}

export function getCharacterSheetUrl(characterId): string {
  return getUrlWithParams(`${BASE_PATHNAME}/${characterId}`);
}

export function getCharacterListingUrl(): string {
  return getUrlWithParams(`${BASE_PATHNAME}`);
}

export function getUrlWithParams(url?: string): string {
  const baseUrl = url ?? BASE_PATHNAME;
  return window.location.search
    ? `${baseUrl}${window.location.search}`
    : baseUrl;
}
